import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'font-awesome/css/font-awesome.min.css';
import AppUpdateBanner from './AppUpdateBanner';
import StorePickupAlerts from './StorePickupAlerts';

// ─── outside component to avoid re-creation on every render ──────────────────
const emptyCount = () => ({
  delivered: 0, ready: 0, dropenroute: 0, pickup: 0, failed: 0,
  assigned: 0, unassigned: 0, inscan: 0, pickedup: 0,
  linehaultransit: 0, undefinedStatus: 0,
});

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return { label: 'Good Morning',  emoji: '☀️',  sub: "Ready to track today's deliveries?" };
  if (h < 17) return { label: 'Good Afternoon', emoji: '⛅️', sub: 'Keep up the great work!' };
  return          { label: 'Good Evening',   emoji: '🌙',  sub: "Here's how the day went." };
};

/**
 * Hands its children the width actually available to them, kept current as the window or layout
 * changes. Lets the SVG charts draw at the card's real width - filling it on a desktop, fitting a
 * phone - instead of a fixed 600px that overflowed small screens and left wide cards half empty.
 * `minWidth` keeps a long series readable: past it the chart scrolls sideways rather than squash.
 */
const MeasuredWidth = ({ minWidth = 0, children }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: '100%', overflowX: 'auto' }}>
      {width > 0 ? children(Math.max(width, minWidth)) : null}
    </div>
  );
};

export default function Dashboard() {
  const API_URL = process.env.REACT_APP_API_URL;

  // ── view state ──────────────────────────────────────────────────────────────
  const [viewMode, setViewMode]   = useState('cards');
  const [loading,  setLoading]    = useState(false);

  // ── graph view ──────────────────────────────────────────────────────────────
  const [fromDate,    setFromDate]    = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [toDate,      setToDate]      = useState(moment().format('YYYY-MM-DD'));
  const [granularity, setGranularity] = useState('DAILY');
  const [dashboardData, setDashboardData] = useState(null);

  // ── cards view ──────────────────────────────────────────────────────────────
  const [selectedDate,  setSelectedDate]  = useState(moment().format('YYYY-MM-DD'));
  const [todayCounts,   setTodayCounts]   = useState(emptyCount());
  const [ydayCounts,    setYdayCounts]    = useState(emptyCount());

  // backward-compat alias (chart sections reference `counts`)
  const counts = todayCounts;

  // ── parse API response ──────────────────────────────────────────────────────
  const parseResponseToCounts = (data) => {
    const c = emptyCount();
    if (data?.data?.orders) {
      data.data.orders.forEach(o => {
        switch (o.status) {
          case 'DELIVERED':                   c.delivered       += o.count; break;
          case 'READY':                       c.ready           += o.count; break;
          case 'DROP_ENROUTE':                c.dropenroute     += o.count; break;
          case 'PICKUP_ENROUTE':              c.pickup          += o.count; break;
          case 'FAILED':                      c.failed          += o.count; break;
          case 'ASSIGNED':                    c.assigned        += o.count; break;
          case 'DESTINATION_LINEHAUL_DEPART': c.linehaultransit += o.count; break;
          case 'UNASSIGNED':                  c.unassigned      += o.count; break;
          case 'PICKED_UP':                   c.pickedup        += o.count; break;
          case 'INSCAN_AT_WAREHOUSE':         c.inscan          += o.count; break;
          default:                            c.undefinedStatus += o.count; break;
        }
      });
    }
    return c;
  };

  // ── fetch today AND yesterday simultaneously ────────────────────────────────
  const fetchCardsCounts = useCallback(async (date) => {
    setLoading(true);
    const prevDay = moment(date).subtract(1, 'day').format('YYYY-MM-DD');
    try {
      const [todayRes, ydayRes] = await Promise.all([
        axios.get(`${API_URL}shypvdriverapp/jobs/getJobsByStatusAndDate`, { params: { date } }),
        axios.get(`${API_URL}shypvdriverapp/jobs/getJobsByStatusAndDate`, { params: { date: prevDay } }),
      ]);
      setTodayCounts(parseResponseToCounts(todayRes.data));
      setYdayCounts(parseResponseToCounts(ydayRes.data));
    } catch (err) {
      console.error('Error fetching card data:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // kept for any external callers that may reference this name
  const fetchSingleDayCounts = fetchCardsCounts;

  // Fetch dashboard graph data from new consolidated API
  const fetchDashboardGraphData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}shypvdriverapp/api/dashboard/graph`, {
        fromDate,
        toDate,
        granularity,
        partnerId: null,
        driverId: null,
        shipmentType: null,
        orderType: null
      });
      
      if (response.data?.success && response.data?.data) {
        setDashboardData(response.data.data);
      } else {
        console.error('API response unsuccessful:', response.data?.message);
        setDashboardData(null);
      }
    } catch (error) {
      console.error('Error fetching dashboard graph data:', error);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [API_URL, fromDate, toDate, granularity]);

  // Effect for cards view
  useEffect(() => {
    if (viewMode === 'cards') {
      fetchCardsCounts(selectedDate);
    }
  }, [selectedDate, viewMode, fetchCardsCounts]);

  // Effect for graph view
  useEffect(() => {
    if (viewMode === 'graph') {
      fetchDashboardGraphData();
    }
  }, [viewMode, fetchDashboardGraphData]);

  // Extract data from dashboard response
  const summary = dashboardData?.summary || {};
  const timeSeries = dashboardData?.timeSeries || [];
  const statusBreakdown = dashboardData?.statusBreakdown || {};
  const orderTypeBreakdown = dashboardData?.orderTypeBreakdown || {};
  const paymentTypeBreakdown = dashboardData?.paymentTypeBreakdown || {};
  const topAreas = dashboardData?.topAreas || [];
  const deliveryMetrics = dashboardData?.deliveryMetrics || {};

  // Format large numbers
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  // Format currency
  const formatCurrency = (num) => {
    if (num === null || num === undefined) return 'AED 0';
    if (num >= 1000000) return 'AED ' + (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return 'AED ' + (num / 1000).toFixed(1) + 'k';
    return 'AED ' + num.toFixed(0);
  };

  // ── greeting & live context ─────────────────────────────────────────────────
  const greeting = getGreeting();
  const nowLabel = moment().format('dddd, MMMM D YYYY • h:mm A');
  const isToday  = moment(selectedDate).isSame(moment(), 'day');
  const dateLabel = isToday ? 'Today' : moment(selectedDate).format('dddd, MMMM D YYYY');
  const prevDateLabel = moment(selectedDate).subtract(1, 'day').format('MMM D');

  // ── per-key delta (today minus yesterday) ───────────────────────────────────
  const delta = (key) => (todayCounts[key] || 0) - (ydayCounts[key] || 0);

  // ── hero headline KPIs ───────────────────────────────────────────────────────
  const totalToday = Object.values(todayCounts).reduce((s, v) => s + v, 0);
  const totalYday  = Object.values(ydayCounts).reduce((s, v) => s + v, 0);
  const successRate = totalToday > 0
    ? ((todayCounts.delivered / totalToday) * 100).toFixed(1)
    : '0.0';

  // Card configuration for legacy cards view
  const cardConfig = [
    { key: 'linehaultransit', label: 'In Transit', bgColor: '#3b82f6', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-truck' },
    { key: 'inscan', label: 'In Scan', bgColor: '#0ea5e9', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-archive' },
    { key: 'dropenroute', label: 'Out For Delivery', bgColor: '#06b6d4', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-truck' },
    { key: 'delivered', label: 'Delivered', bgColor: '#10b981', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-check-circle' },
    { key: 'unassigned', label: 'Unassigned', bgColor: '#f59e0b', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-clipboard' },
    { key: 'pickup', label: 'Out For Pickup', bgColor: '#6366f1', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-cube' },
    { key: 'pickedup', label: 'Picked Up', bgColor: '#14b8a6', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-hand-paper-o' },
    { key: 'failed', label: 'Failed', bgColor: '#ef4444', iconBg: 'rgba(255,255,255,0.25)', icon: 'fa-times-circle' },
  ];

  // Summary cards config for graph view (from new API)
  const summaryCardsConfig = [
    { key: 'totalOrders', label: 'Total Orders', bgColor: '#3b82f6', icon: 'fa-cube' },
    { key: 'deliveredOrders', label: 'Delivered', bgColor: '#10b981', icon: 'fa-check-circle' },
    { key: 'pendingOrders', label: 'Pending', bgColor: '#f59e0b', icon: 'fa-clock-o' },
    { key: 'failedOrders', label: 'Failed', bgColor: '#ef4444', icon: 'fa-times-circle' },
    { key: 'totalCodCollected', label: 'COD Collected', bgColor: '#8b5cf6', icon: 'fa-money', isCurrency: true },
    { key: 'deliverySuccessRate', label: 'Success Rate', bgColor: '#06b6d4', icon: 'fa-line-chart', isPercent: true },
    { key: 'avgOrdersPerDay', label: 'Avg/Day', bgColor: '#14b8a6', icon: 'fa-calendar' },
    { key: 'unassignedOrders', label: 'Unassigned', bgColor: '#6b7280', icon: 'fa-question-circle' },
  ];

  // Status colors for charts
  const statusColors = {
    DELIVERED: '#22C55E',
    ASSIGNED_TO_DRIVER: '#3B82F6',
    PENDING: '#F59E0B',
    FAILED: '#EF4444',
    UNASSIGNED: '#6B7280',
    IN_TRANSIT: '#0EA5E9',
    CANCELLED: '#DC2626',
  };

  // Line Chart Component for time series
  const LineChart = ({ data, dataKeys, colors, labels, height = 280 }) => {
    if (!data || data.length === 0) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;

    return (
      <div style={{ padding: '10px 0' }}>
        <MeasuredWidth minWidth={data.length * 36 + 90}>
          {(measuredWidth) => renderLineSvg(measuredWidth)}
        </MeasuredWidth>
        {/* Legend */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '16px', flexWrap: 'wrap' }}>
          {dataKeys.map((key, i) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '16px', height: '3px', borderRadius: '2px', backgroundColor: colors[i] }} />
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{labels[i]}</span>
            </div>
          ))}
        </div>
      </div>
    );

    function renderLineSvg(chartWidth) {
    const padding = { top: 20, right: 30, bottom: 40, left: 60 };
    const chartHeight = height;
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;
    
    const maxValue = Math.max(...data.flatMap(d => dataKeys.map(k => d[k] || 0)), 1);
    
    const getX = (index) => padding.left + (index / (data.length - 1 || 1)) * innerWidth;
    const getY = (value) => padding.top + innerHeight - (value / maxValue) * innerHeight;
    
    return (
        <svg width={chartWidth} height={chartHeight} style={{ display: 'block' }}>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((percent) => (
            <g key={percent}>
              <line
                x1={padding.left}
                y1={padding.top + innerHeight * (1 - percent / 100)}
                x2={chartWidth - padding.right}
                y2={padding.top + innerHeight * (1 - percent / 100)}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray={percent > 0 ? "4,4" : "0"}
              />
              <text
                x={padding.left - 10}
                y={padding.top + innerHeight * (1 - percent / 100) + 4}
                fontSize="11"
                fill="#94a3b8"
                textAnchor="end"
              >
                {formatNumber(Math.round(maxValue * percent / 100))}
              </text>
            </g>
          ))}
          
          {/* Lines */}
          {dataKeys.map((key, keyIndex) => {
            const points = data.map((d, i) => `${getX(i)},${getY(d[key] || 0)}`).join(' ');
            return (
              <g key={key}>
                {/* Area under line */}
                <polygon
                  points={`${getX(0)},${padding.top + innerHeight} ${points} ${getX(data.length - 1)},${padding.top + innerHeight}`}
                  fill={colors[keyIndex]}
                  fillOpacity="0.1"
                />
                {/* Line */}
                <polyline
                  points={points}
                  fill="none"
                  stroke={colors[keyIndex]}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Data points */}
                {data.map((d, i) => (
                  <circle
                    key={i}
                    cx={getX(i)}
                    cy={getY(d[key] || 0)}
                    r="4"
                    fill="#fff"
                    stroke={colors[keyIndex]}
                    strokeWidth="2"
                  >
                    <title>{`${labels[keyIndex]}: ${formatNumber(d[key] || 0)}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
          
          {/* X-axis labels */}
          {data.map((item, i) => (
            <text
              key={i}
              x={getX(i)}
              y={chartHeight - 10}
              fontSize="10"
              fill="#64748b"
              textAnchor="middle"
            >
              {item.label || moment(item.date).format('MMM D')}
            </text>
          ))}
        </svg>
    );
    }
  };

  // Bar Chart Component
  const BarChart = ({ data, dataKey, color, label, height = 200 }) => {
    if (!data || data.length === 0) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;
    
    const maxValue = Math.max(...data.map(d => d[dataKey] || 0), 1);
    const chartHeight = height;

    return (
      <div style={{ padding: '10px 0' }}>
        <MeasuredWidth minWidth={data.length * 24 + 70}>
          {(chartWidth) => {
            // One slot per bar across the measured width; the bar is centred in its slot.
            const slot = (chartWidth - 70) / data.length;
            const barWidth = Math.max(12, Math.min(50, slot - 10));
            const barX = (i) => 60 + i * slot + (slot - barWidth) / 2;
            return (
        <svg width={chartWidth} height={chartHeight + 40} style={{ display: 'block' }}>
          {/* Y-axis grid lines */}
          {[0, 25, 50, 75, 100].map((percent) => (
            <line
              key={percent}
              x1="50"
              y1={chartHeight - (chartHeight * percent / 100)}
              x2={chartWidth - 20}
              y2={chartHeight - (chartHeight * percent / 100)}
              stroke="#e2e8f0"
              strokeWidth="1"
              strokeDasharray={percent > 0 ? "4,4" : "0"}
            />
          ))}
          
          {/* Bars */}
          {data.map((item, i) => {
            const value = item[dataKey] || 0;
            const barHeight = maxValue > 0 ? (value / maxValue) * (chartHeight - 20) : 0;
            return (
              <g key={i}>
                <rect
                  x={barX(i)}
                  y={chartHeight - barHeight}
                  width={barWidth}
                  height={barHeight}
                  fill={color}
                  rx="4"
                  style={{ transition: 'all 0.3s ease' }}
                >
                  <title>{`${label}: ${formatNumber(value)}`}</title>
                </rect>
                <text
                  x={barX(i) + barWidth / 2}
                  y={chartHeight + 20}
                  fontSize="10"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {item.label || item.cityOrArea || ''}
                </text>
              </g>
            );
          })}
        </svg>
            );
          }}
        </MeasuredWidth>
      </div>
    );
  };

  // Pie/Donut Chart Component
  const PieChart = ({ data, colors, isDonut = false, size = 200 }) => {
    if (!data || Object.keys(data).length === 0) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;
    
    const entries = Object.entries(data).filter(([_, v]) => v > 0);
    if (entries.length === 0) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;
    
    const total = entries.reduce((sum, [_, v]) => sum + v, 0);
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 10;
    const innerRadius = isDonut ? radius * 0.6 : 0;
    
    let currentAngle = -90;
    
    const getPath = (startAngle, endAngle, outerR, innerR) => {
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;
      const largeArc = endAngle - startAngle > 180 ? 1 : 0;
      
      const x1 = cx + outerR * Math.cos(startRad);
      const y1 = cy + outerR * Math.sin(startRad);
      const x2 = cx + outerR * Math.cos(endRad);
      const y2 = cy + outerR * Math.sin(endRad);
      const x3 = cx + innerR * Math.cos(endRad);
      const y3 = cy + innerR * Math.sin(endRad);
      const x4 = cx + innerR * Math.cos(startRad);
      const y4 = cy + innerR * Math.sin(startRad);
      
      if (innerR === 0) {
        return `M ${cx} ${cy} L ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      }
      return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${largeArc} 0 ${x4} ${y4} Z`;
    };

    const defaultColors = ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#6B7280', '#0EA5E9', '#8B5CF6', '#EC4899'];
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <svg width={size} height={size}>
          {entries.map(([key, value], i) => {
            const angle = (value / total) * 360;
            const path = getPath(currentAngle, currentAngle + angle, radius, innerRadius);
            const color = colors?.[key] || statusColors[key] || defaultColors[i % defaultColors.length];
            const midAngle = currentAngle + angle / 2;
            currentAngle += angle;
            
            return (
              <path
                key={key}
                d={path}
                fill={color}
                stroke="#fff"
                strokeWidth="2"
                style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
              >
                <title>{`${key}: ${formatNumber(value)} (${((value / total) * 100).toFixed(1)}%)`}</title>
              </path>
            );
          })}
          {isDonut && (
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="24" fontWeight="700" fill="#1e293b">
              {formatNumber(total)}
            </text>
          )}
        </svg>
        
        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', maxWidth: '300px' }}>
          {entries.map(([key, value], i) => {
            const color = colors?.[key] || statusColors[key] || defaultColors[i % defaultColors.length];
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: color }} />
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  {key.replace(/_/g, ' ')} ({formatNumber(value)})
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Horizontal Bar Chart for Top Areas
  const HorizontalBarChart = ({ data, height = 250 }) => {
    if (!data || data.length === 0) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;
    
    const maxValue = Math.max(...data.map(d => d.orderCount || 0), 1);
    const barHeight = 32;
    const chartHeight = Math.max(height, data.length * (barHeight + 12));
    
    return (
      <div style={{ padding: '10px 0' }}>
        {data.map((item, i) => {
          const width = ((item.orderCount || 0) / maxValue) * 100;
          return (
            <div key={i} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{item.cityOrArea}</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {formatNumber(item.orderCount)} ({(item.deliveryRate || 0).toFixed(1)}%)
                </span>
              </div>
              <div style={{ height: barHeight, backgroundColor: '#f1f5f9', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${width}%`,
                  backgroundColor: '#3b82f6',
                  borderRadius: '6px',
                  transition: 'width 0.5s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: '8px',
                }}>
                  {width > 15 && (
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#fff' }}>
                      {formatNumber(item.deliveredCount || 0)} delivered
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // KPI Card Component
  const KpiCard = ({ title, value, change, trend, icon, color }) => (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          backgroundColor: color + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <i className={`fa ${icon}`} style={{ fontSize: '16px', color: color }} />
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
        {value}
      </div>
      {(change !== undefined && change !== null) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className={`fa fa-arrow-${trend === 'UP' ? 'up' : trend === 'DOWN' ? 'down' : 'right'}`} 
             style={{ fontSize: '12px', color: trend === 'UP' ? '#22C55E' : trend === 'DOWN' ? '#EF4444' : '#64748b' }} />
          <span style={{ fontSize: '12px', color: trend === 'UP' ? '#22C55E' : trend === 'DOWN' ? '#EF4444' : '#64748b', fontWeight: '500' }}>
            {Math.abs(change).toFixed(1)}% vs previous
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* ── Global CSS ── */}
      <style>{`
        @keyframes spin       { to { transform: rotate(360deg); } }
        @keyframes fadeUp     { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes heroSlide  { from { opacity: 0; transform: translateY(-18px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .dash-hero    { animation: heroSlide 0.45s cubic-bezier(0.22,1,0.36,1) both; }
        .dash-card { animation: fadeUp 0.35s ease-out both; transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .dash-card:hover  { transform: translateY(-5px); box-shadow: 0 16px 40px -10px rgba(0,0,0,0.18) !important; }
        .chart-section    { animation: fadeUp 0.4s ease-out both; }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(1) opacity(0.6); cursor: pointer; }

        /* Centered max-width wrapper so the dashboard doesn't stretch
           edge-to-edge on 2K+ monitors. Cards stay legible, not sparse. */
        .dash-container {
          max-width: 1600px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
        }
        /* Status-cards grid uses auto-fit with a tighter max so cards don't
           inflate past a comfortable reading width on wide screens. */
        .dash-status-grid {
          display: grid;
          gap: 18px;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }
        /* Hero KPI pills: four across beside the greeting, 2 x 2 under it on a phone. */
        .dash-hero-kpis {
          display: grid;
          grid-template-columns: repeat(4, minmax(112px, 1fr));
          gap: 10px;
          flex: 0 1 560px;
          min-width: 0;
        }
        /* Analytics summary tiles. */
        .dash-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        /* Chart rows: two charts side by side when there is room, one per row otherwise.
           min(100%, 450px) so a narrow screen never gets a column wider than itself. */
        .dash-chart-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 450px), 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }
        /* Tablet — tighter page padding, slightly smaller card chrome. */
        @media (max-width: 1100px) {
          .dash-hero     { padding: 24px 20px 20px !important; }
          .dash-body     { padding: 22px 20px !important; background-attachment: scroll !important; }
          .dash-card     { padding: 18px 18px 14px !important; }
          .dash-hero-kpis { flex: 1 1 100%; }
        }
        /* Phone — single column for status cards, simpler hero. */
        @media (max-width: 720px) {
          .dash-hero     { padding: 20px 14px 16px !important; }
          .dash-hero h1  { font-size: 24px !important; }
          .dash-body     { padding: 16px 14px !important; }
          .dash-status-grid { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)) !important; gap: 12px !important; }
          .dash-card     { padding: 14px 14px 12px !important; }
          .dash-card-value { font-size: 28px !important; }
          .dash-card-watermark { font-size: 38px !important; }
          .dash-hero     { margin: 12px 10px 0 !important; border-radius: 16px !important; }
          .dash-hero-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .dash-hero-kpi-value { font-size: 22px !important; }
          .dash-panel    { padding: 16px !important; }
          .dash-chart-grid { gap: 14px; }
          .dash-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
          .dash-summary-grid .dash-card { padding: 12px !important; gap: 10px !important; }
          .dash-summary-icon { width: 34px !important; height: 34px !important; }
          .dash-summary-value { font-size: 18px !important; }
        }
      `}</style>

      {/* ── Loading overlay ── */}
      {loading && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(241,245,249,0.82)',
          backdropFilter: 'blur(5px)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            width: 52, height: 52,
            border: '3px solid #dbeafe',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.75s linear infinite',
          }} />
          <p style={{ marginTop: 16, color: '#475569', fontWeight: 600, fontSize: 14 }}>
            Loading data…
          </p>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          DRIVER APP RELEASE — announces the current build and hands over the
          share link. Renders nothing when there is no release or it was dismissed.
      ════════════════════════════════════════════════════════════════════════ */}
      <div style={{ margin: '16px 16px 0' }}>
        <AppUpdateBanner />
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          HERO BANNER
      ════════════════════════════════════════════════════════════════════════ */}
      <div className="dash-hero" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 55%, #2563eb 100%)',
        padding: '32px 32px 26px',
        margin: '16px 16px 0',
        borderRadius: 20,
        boxShadow: '0 8px 32px rgba(15,23,42,0.28)',
        color: '#fff',
        overflow: 'hidden',
      }}>
       <div className="dash-container">
        {/* Top row: greeting left, KPI pills right */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', flexWrap: 'wrap',
          gap: 20, marginBottom: 28,
        }}>
          {/* Greeting */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <span style={{ fontSize: 36, lineHeight: 1 }}>{greeting.emoji}</span>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: '-0.5px' }}>
                {greeting.label}
              </h1>
            </div>
            <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: 14 }}>{nowLabel}</p>
            <p style={{ margin: '6px 0 0', opacity: 0.55, fontSize: 13 }}>{greeting.sub}</p>
          </div>

          {/* Hero KPI pills — only meaningful when in cards view */}
          {viewMode === 'cards' && (
            <div className="dash-hero-kpis">
              {[
                {
                  label: 'Total Today',
                  value: formatNumber(totalToday),
                  sub: totalYday > 0 ? `${totalToday - totalYday >= 0 ? '+' : ''}${totalToday - totalYday} vs ${prevDateLabel}` : '',
                  icon: 'fa-cube',
                  accent: 'rgba(255,255,255,0.13)',
                  subColor: totalToday >= totalYday ? '#6ee7b7' : '#fca5a5',
                },
                {
                  label: 'Delivered',
                  value: formatNumber(todayCounts.delivered),
                  sub: delta('delivered') !== 0 ? `${delta('delivered') >= 0 ? '+' : ''}${delta('delivered')} vs ${prevDateLabel}` : 'Same as yesterday',
                  icon: 'fa-check-circle',
                  accent: 'rgba(16,185,129,0.28)',
                  subColor: delta('delivered') >= 0 ? '#6ee7b7' : '#fca5a5',
                },
                {
                  label: 'Failed',
                  value: formatNumber(todayCounts.failed),
                  sub: delta('failed') !== 0 ? `${delta('failed') >= 0 ? '+' : ''}${delta('failed')} vs ${prevDateLabel}` : 'Same as yesterday',
                  icon: 'fa-times-circle',
                  accent: 'rgba(239,68,68,0.28)',
                  subColor: delta('failed') <= 0 ? '#6ee7b7' : '#fca5a5',
                },
                {
                  label: 'Success Rate',
                  value: successRate + '%',
                  sub: dateLabel,
                  icon: 'fa-line-chart',
                  accent: 'rgba(99,102,241,0.28)',
                  subColor: 'rgba(255,255,255,0.55)',
                },
              ].map(kpi => (
                <div key={kpi.label} style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  background: kpi.accent,
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: 16, padding: '14px 12px',
                  backdropFilter: 'blur(8px)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                    <i className={`fa ${kpi.icon}`} style={{ fontSize: 13, opacity: 0.8 }} />
                    <span style={{ fontSize: 11, opacity: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      {kpi.label}
                    </span>
                  </div>
                  <div className="dash-hero-kpi-value" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, marginBottom: 5 }}>{kpi.value}</div>
                  {kpi.sub && (
                    <div style={{ fontSize: 11, color: kpi.subColor, fontWeight: 600 }}>{kpi.sub}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom row of hero: view toggle + date controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Cards / Graph toggle */}
          <div style={{
            display: 'flex',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 12, padding: 3,
            border: '1px solid rgba(255,255,255,0.15)',
          }}>
            {[['cards', 'fa-th-large', 'Cards'], ['graph', 'fa-bar-chart', 'Analytics']].map(([mode, icon, label]) => (
              <button key={mode} onClick={() => setViewMode(mode)} style={{
                padding: '8px 18px', border: 'none', borderRadius: 10,
                cursor: 'pointer', fontWeight: 700, fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 6,
                background: viewMode === mode ? '#fff' : 'transparent',
                color:      viewMode === mode ? '#1e3a8a' : 'rgba(255,255,255,0.75)',
                transition: 'all 0.18s',
                boxShadow:  viewMode === mode ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
              }}>
                <i className={`fa ${icon}`} style={{ fontSize: 13 }} />
                {label}
              </button>
            ))}
          </div>

          {viewMode === 'cards' ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.1)',
              padding: '9px 14px', borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <i className="fa fa-calendar-o" style={{ fontSize: 14, opacity: 0.75 }} />
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                style={{
                  border: 'none', background: 'transparent',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', outline: 'none',
                  colorScheme: 'dark',
                }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {/* Granularity */}
              <div style={{
                display: 'flex', background: 'rgba(255,255,255,0.1)',
                borderRadius: 12, padding: 3,
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
                {['DAILY', 'WEEKLY', 'MONTHLY'].map(g => (
                  <button key={g} onClick={() => setGranularity(g)} style={{
                    padding: '7px 14px', border: 'none', borderRadius: 10,
                    cursor: 'pointer', fontWeight: 600, fontSize: 12,
                    background: granularity === g ? '#fff' : 'transparent',
                    color:      granularity === g ? '#1e3a8a' : 'rgba(255,255,255,0.75)',
                    transition: 'all 0.18s',
                  }}>
                    {g.charAt(0) + g.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>

              {[
                { label: 'From', val: fromDate, set: setFromDate },
                { label: 'To',   val: toDate,   set: setToDate   },
              ].map(({ label, val, set }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: 'rgba(255,255,255,0.1)',
                  padding: '9px 13px', borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.18)',
                }}>
                  <span style={{ fontSize: 11, opacity: 0.65, fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
                  <input
                    type="date" value={val} onChange={e => set(e.target.value)}
                    style={{
                      border: 'none', background: 'transparent',
                      color: '#fff', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', outline: 'none', colorScheme: 'dark',
                    }}
                  />
                </div>
              ))}

              <button onClick={fetchDashboardGraphData} disabled={loading} style={{
                padding: '10px 18px',
                background: loading ? 'rgba(255,255,255,0.5)' : '#fff',
                color: '#1e3a8a', border: 'none', borderRadius: 12,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 700, fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 7,
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}>
                <i className="fa fa-refresh" style={{ fontSize: 13 }} />
                Refresh
              </button>
            </div>
          )}
        </div>
       </div>{/* /.dash-container hero */}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          BODY
      ════════════════════════════════════════════════════════════════════════ */}
      <div className="dash-body" style={{
        flex: 1,
        padding: '26px 32px',
        backgroundImage: `url('/images/background.svg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}>
       <div className="dash-container">

        {/* ── STORE PICKUP ALERTS — new store pickups and ones waiting too long; polls on
            its own every 20s, independently of the dashboard's date filters. Inside the
            container so it lines up with the cards below it. */}
        <div style={{ marginBottom: 24 }}>
          <StorePickupAlerts />
        </div>

        {/* ── CARDS VIEW ─────────────────────────────────────────────────────── */}
        {viewMode === 'cards' && (
          <>
            {/* Section heading */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 22, flexWrap: 'wrap', gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 4, height: 22, borderRadius: 4,
                  background: 'linear-gradient(180deg, #3b82f6, #6366f1)',
                }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                  {dateLabel} — Order Status
                </span>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '6px 12px',
              }}>
                <i className="fa fa-info-circle" style={{ color: '#94a3b8', fontSize: 13 }} />
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Delta compares to <strong>{prevDateLabel}</strong>
                </span>
              </div>
            </div>

            {/* Status cards grid — auto-fit so on wide screens cards keep a
                comfortable max width instead of stretching past readability. */}
            <div className="dash-status-grid">
              {cardConfig.map((card, i) => {
                const today     = todayCounts[card.key] || 0;
                const d         = delta(card.key);
                const isPos     = d > 0;
                const isNeg     = d < 0;
                const dColor    = isPos ? '#10b981' : isNeg ? '#ef4444' : '#94a3b8';
                const dBg       = isPos ? '#d1fae5' : isNeg ? '#fee2e2' : '#f1f5f9';
                const dIcon     = isPos ? 'fa-arrow-up' : isNeg ? 'fa-arrow-down' : 'fa-minus';
                const dText     = d === 0
                  ? `Same as ${prevDateLabel}`
                  : `${isPos ? '+' : ''}${d} vs ${prevDateLabel}`;

                return (
                  <div
                    key={card.key}
                    className="dash-card"
                    style={{
                      background: '#ffffff',
                      borderRadius: 16,
                      padding: '18px 18px 14px',
                      boxShadow: '0 2px 16px -3px rgba(0,0,0,0.12)',
                      border: '1px solid #e8edf2',
                      borderTop: `4px solid ${card.bgColor}`,
                      position: 'relative',
                      overflow: 'hidden',
                      animationDelay: `${i * 0.045}s`,
                    }}
                  >
                    {/* Watermark icon — smaller + lower opacity so it doesn't
                        dominate when the card is on the wider end. */}
                    <i className={`fa ${card.icon} dash-card-watermark`} style={{
                      position: 'absolute', right: 12, top: 12,
                      fontSize: 46, color: card.bgColor, opacity: 0.08,
                      pointerEvents: 'none',
                    }} />

                    {/* Coloured icon badge */}
                    <div style={{
                      width: 38, height: 38, borderRadius: 12,
                      background: card.bgColor + '1a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 12,
                    }}>
                      <i className={`fa ${card.icon}`} style={{ fontSize: 17, color: card.bgColor }} />
                    </div>

                    {/* Value */}
                    <div className="dash-card-value" style={{
                      fontSize: 34, fontWeight: 800, color: '#0f172a',
                      lineHeight: 1, marginBottom: 4,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {formatNumber(today)}
                    </div>

                    {/* Label */}
                    <div style={{
                      fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 12,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {card.label}
                    </div>

                    {/* Delta badge */}
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: dBg, borderRadius: 20,
                      padding: '4px 10px',
                    }}>
                      <i className={`fa ${dIcon}`} style={{ fontSize: 10, color: dColor }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: dColor }}>
                        {dText}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Yesterday summary row */}
            <div style={{
              marginTop: 28,
              background: '#ffffff',
              borderRadius: 16,
              padding: '18px 24px',
              boxShadow: '0 1px 6px -2px rgba(0,0,0,0.09)',
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14,
              }}>
                {prevDateLabel} (Yesterday) — Reference
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 20,
              }}>
                {cardConfig.map(card => (
                  <div key={card.key} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: card.bgColor,
                    }} />
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      <span style={{ fontWeight: 700, color: '#374151' }}>
                        {formatNumber(ydayCounts[card.key] || 0)}
                      </span>
                      {' '}{card.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── GRAPH / ANALYTICS VIEW ─────────────────────────────────────────── */}
        {viewMode === 'graph' && (
          <div className="chart-section">
            {/* Summary Stats Cards Row */}
            <div className="dash-summary-grid">
              {summaryCardsConfig.map((card, index) => {
                let value = summary[card.key];
                if (card.isCurrency) value = formatCurrency(value);
                else if (card.isPercent) value = (value || 0).toFixed(1) + '%';
                else value = formatNumber(value || 0);

                return (
                  <div
                    key={card.key}
                    className="dash-card"
                    style={{
                      backgroundColor: card.bgColor,
                      borderRadius: 16,
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      animationDelay: `${index * 0.05}s`,
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <div className="dash-summary-icon" style={{
                      width: 48, height: 48, borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <i className={`fa ${card.icon}`} style={{ fontSize: 20, color: '#fff' }} />
                    </div>
                    <div>
                      <p className="dash-summary-value" style={{ fontSize: 24, fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.2 }}>
                        {value}
                      </p>
                      <p style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0' }}>
                        {card.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Charts Grid */}
            <div className="dash-chart-grid">
              <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Orders Trend</h3>
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{granularity.toLowerCase()} order performance</p>
                </div>
                <LineChart
                  data={timeSeries}
                  dataKeys={['totalOrders', 'deliveredOrders', 'failedOrders', 'pendingOrders']}
                  colors={['#3B82F6', '#22C55E', '#EF4444', '#F59E0B']}
                  labels={['Total', 'Delivered', 'Failed', 'Pending']}
                />
              </div>
              <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Order Status Distribution</h3>
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Breakdown by status</p>
                </div>
                <PieChart data={statusBreakdown} colors={statusColors} size={220} />
              </div>
            </div>

            <div className="dash-chart-grid">
              <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Delivery vs Pickup</h3>
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Order type distribution</p>
                </div>
                <PieChart data={orderTypeBreakdown} colors={{ DROP: '#3B82F6', PICK_UP: '#22C55E' }} isDonut size={220} />
              </div>
              <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Payment Type Distribution</h3>
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>COD vs Prepaid</p>
                </div>
                <PieChart data={paymentTypeBreakdown} colors={{ COD: '#8B5CF6', PREPAID: '#06B6D4', NOT_SET: '#6B7280' }} size={220} />
              </div>
            </div>

            <div className="dash-chart-grid">
              <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Top Delivery Areas</h3>
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Performance by location</p>
                </div>
                <HorizontalBarChart data={topAreas.slice(0, 6)} />
              </div>
              <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>COD Collection Trend</h3>
                  <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{granularity.toLowerCase()} collection amount</p>
                </div>
                <BarChart data={timeSeries} dataKey="codCollected" color="#8B5CF6" label="COD Collected" />
              </div>
            </div>

            {/* KPI panel */}
            <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)', marginBottom: 20 }}>
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Delivery Performance Metrics</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Key performance indicators</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <KpiCard title="Success Rate"         value={(deliveryMetrics.overallSuccessRate || 0).toFixed(1) + '%'}      change={deliveryMetrics.successRateChange} trend={deliveryMetrics.trend} icon="fa-check-circle"     color="#22C55E" />
                <KpiCard title="Avg Deliveries/Day"   value={formatNumber(deliveryMetrics.avgDeliveriesPerDay || 0)}                                                                               icon="fa-calendar-check-o" color="#3B82F6" />
                <KpiCard title="Total Attempts"       value={formatNumber(deliveryMetrics.totalAttempts || 0)}                                                                                     icon="fa-refresh"          color="#F59E0B" />
                <KpiCard title="Successful Deliveries"value={formatNumber(deliveryMetrics.successfulDeliveries || 0)}                                                                              icon="fa-thumbs-up"        color="#22C55E" />
                <KpiCard title="Failed Deliveries"    value={formatNumber(deliveryMetrics.failedDeliveries || 0)}                                                                                  icon="fa-times-circle"     color="#EF4444" />
                <KpiCard title="Pending Deliveries"   value={formatNumber(deliveryMetrics.pendingDeliveries || 0)}                                                                                 icon="fa-clock-o"          color="#F59E0B" />
              </div>
            </div>

            {/* Delivery rate trend */}
            <div className="dash-panel" style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px -2px rgba(0,0,0,0.06)' }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>Delivery Success Rate Over Time</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{granularity.toLowerCase()} success rate %</p>
              </div>
              <LineChart data={timeSeries} dataKeys={['deliveryRate']} colors={['#22C55E']} labels={['Delivery Rate %']} height={200} />
            </div>
          </div>
        )}
       </div>{/* /.dash-container body */}
      </div>
    </div>
  );
}
